import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import * as d3 from 'd3';
import withStyles from 'isomorphic-style-loader/withStyles';
import s from './BettingGraph.css';

const xDim = 600;
const yDim = 480;
const xPadding = 100;
const yPadding = 40;
const innerXDim = xDim - 2 * xPadding;
const innerYDim = yDim - 2 * yPadding;
const baseDepth = 9;
const maxCacheSize = 4096;
const magnificationRatio = 0.6;
const depthLimit = 51;

class BettingGraph extends React.Component {
  static propTypes = {
    alpha: PropTypes.number.isRequired,
    reset: PropTypes.bool,
  }

  componentDidMount() {
    this.reset();
    this.drawGraph(this.props);
  }

  componentWillReceiveProps(nextProps) {
    if ('svg' in this) {
      this.reset();
      this.drawGraph(nextProps);
    }
  }

  setTransform(path, coords) {
    const lineFn = d3.line()
      .x((d) => this.coordTransformX(d.x))
      .y((d) => this.coordTransformY(d.y));

    path
      .attr('d', lineFn(coords));
  }

  getMaxDepth(x0, x1) {
    return Math.floor(baseDepth - Math.log2(x1 - x0));
  }

  getCoords(alpha, x0, x1) {
    const maxDepth = this.getMaxDepth(x0, x1);
    let depth = 0;
    const coords = [];

    while (depth <= maxDepth) {
      const dx = 2 ** -depth;
      let x = Math.floor(x0 / (2 * dx)) * (2 * dx) + dx;
      while (x <= x1) {
        const y = this.getY(x, depth, x0, x1, alpha);
        if (x >= x0) {
          coords.push({
            x, y,
          });
        }
        x += 2 * dx;
      }
      depth++;
    }

    const sortedCoords = _.sortBy(coords, (d) => d.x);
    return sortedCoords;
  }

  getTicks() {
    return [0, 0.25, 0.5, 0.75, 1];
  }

  getY(x, depth, x0, x1, alpha) {
    if (!this.cache) {
      this.cache = {
        0: { x: 0, y: 0, depth: 0, potential: 1 },
        1: { x: 1, y: 1, depth: 0, potential: 0 },
      };
      this.cacheSize = 2;
    }
    if (!this.cache[x]) {
      const basePt = this.cache[x - 2 ** -depth];
      if (!basePt) {
        throw new Error(`Uncached base point: ${x - 2 ** -depth} from ${x}, ${depth}`);
      }
      this.cache[x] = {
        x,
        y: basePt.y + basePt.potential * alpha ** (depth - basePt.depth),
        potential: basePt.potential * alpha ** (depth - basePt.depth - 1) * (1 - alpha),
        depth,
      };
      this.cacheSize ++;
      if (this.cacheSize > maxCacheSize) {
        const minDx = 2 ** -this.getMaxDepth(x0, x1);
        Object.values(this.cache).forEach((entry) => {
          if (entry.depth > baseDepth && (entry.x < x0 - minDx || entry.x > x1)) {
            delete this.cache[entry.x];
            this.cacheSize--;
          }
        });
      }
    }
    return this.cache[x].y;
  }

  handleClick(eventCoords) {
    const center = this.xPropScale(eventCoords[0]);
    const magnificationCenter = Math.min(
      Math.max(center, magnificationRatio / 2),
      1 - magnificationRatio / 2,
    );
    const range = (this.x1 - this.x0);
    const newX0 = this.x0 + (magnificationCenter - magnificationRatio / 2) * range;
    const newX1 = this.x0 + (magnificationCenter + magnificationRatio / 2) * range;

    if (this.getMaxDepth(newX0, newX1) > depthLimit) {
      return;
    }

    this.x0 = newX0;
    this.x1 = newX1;
    this.drawGraph(this.props);
  }

  drawGraph(props) {
    const { x0, x1 } = this;
    if (this.svg) {
      d3.select('#betting-svg').remove();
    }
    this.svg = d3.select('#betting-graph')
      .append('svg')
      .attr('id', 'betting-svg')
      .attr('width', xDim)
      .attr('height', yDim)
      .append('g')
      .attr('transform', `translate(${xPadding},${yPadding})`)
      .attr('width', innerXDim)
      .attr('height', innerYDim);

    this.xScale = d3.scaleLinear()
      .domain([x0, x1])
      .range([0, innerXDim]);
    this.xPropScale = d3.scaleLinear()
      .domain([0, innerXDim])
      .range([0, 1]);
    const coords = this.getCoords(props.alpha, x0, x1);
    const y0 = coords[0].y;
    const y1 = _.last(coords).y;
    this.yScale = d3.scaleLinear()
      .domain([y0, y1])
      .range([innerYDim, 0]);
    this.xAxis = d3.axisBottom()
      .scale(this.xScale);
    this.yAxis = d3.axisLeft()
      .scale(this.yScale);
    this.svg
      .append('g')
      .attr('transform', `translate(0, ${innerYDim})`)
      .call(this.xAxis);
    this.svg
      .append('g')
      .attr('transform', 'translate(0, 0)')
      .call(this.yAxis);

    this.xAxisLabel = this.svg
      // .append('g')
      .append('text')
      .text('x')
      .attr('x', innerXDim / 2 - 4)
      .attr('y', innerYDim + 35);
    this.yAxisLabel = this.svg
      // .append('g')
      .append('text')
      .text('p(x)')
      .attr('x', -60)
      .attr('y', innerYDim / 2 + 4)
      .attr('class', s.label);
    this.probabilityPath = this.svg
      .append('path')
      .attr('class', s.edge)
      .attr('id', 'px-path');
    this.setTransform(this.probabilityPath, coords);

    const self = this;
    this.pointerWindow = this.svg
      .append('rect')
      .attr('width', innerXDim)
      .attr('height', innerYDim)
      .attr('class', s.window)
      .attr('id', 'pointer-window')
      .on('click', function () {
        const coords2 = d3.mouse(this);
        self.handleClick(coords2);
      });
  }

  updateLines(props) {
    const coords = this.getCoords(props.alpha, this.x0, this.x1);
    this.setTransform(this.probabilityPath, coords);
  }

  coordTransformX(x) {
    return this.xScale(x);
  }

  coordTransformY(y) {
    return this.yScale(y);
  }

  reset() {
    delete this.cache;
    this.x0 = 0;
    this.x1 = 1;
  }

  render() {
    return (
      <div id="betting-graph" />
    );
  }
}

export default withStyles(s)(BettingGraph);



// WEBPACK FOOTER //
// src/components/BettingGraph/BettingGraph.js